import React, { useEffect, useState } from 'react';
import { EditItemProps } from '../../../../toolympus/api/useNewItem';
import { AbsenceEvent, AbsenceEventSchema } from '../data/types';
import { Buttons, FormGrid, MoreActionsMenuButton, PseudoLink2, SimpleDialog, useCopyText } from '../../../../toolympus/components/primitives';
import { FormControlsForFields } from '../../../../toolympus/components/schemed';
import { Divider, Typography } from '@mui/material';
import { DictionarySelectDisplay } from '../../../../toolympus/components/schemed/Select';
import { DateDisplayWithDatetimeTooltip } from '../../../../toolympus/components/schemed/TimeDisplay';

interface Props {
  data: EditItemProps<AbsenceEvent>;
  startCancel: (item: AbsenceEvent) => void;
}

export const getAbsenceKindTitle = (kind: string | undefined) => {
  return (AbsenceEventSchema.kind.valueDict || {})[kind || "no"] || "...";
}

export const EditAbsenceDialog = (props: Props) => {
  const { data, startCancel } = props;
  const copyText = useCopyText();

  if(!data.item) {
    return null;
  }

  return (
    <SimpleDialog
      {...data.dialog}
      maxWidth="xs"
      titleActions={<>
        <MoreActionsMenuButton
          actions={[
            data.item.cancelled_at
              ? null
              : ["cancel", "отменить", () => data.item && startCancel(data.item) ],
            ["copyid", `id: ${data.item?._id || "..."}`, () => copyText(`${data.item?._id}`)],
            data.item.b24_process_id && data.item.b24_element_id
              ? ["copyb24link", `ссылка на Б24`, () => copyText(`https://iclrc.bitrix24.ru/bizproc/processes/${data.item?.b24_process_id}/element/0/${data.item?.b24_element_id}/?list_section_id=`)]
              : null,
          ]}
          />
      </>}
      dialogTitle={getAbsenceKindTitle(data.item?.kind)}
      noFullscreen
      submitOnModEnter
      noSave
      save={() => {}}
      cancelLabel="закрыть">
      <FormGrid columns="1fr" noMargin>
        <Typography variant="caption" color="textSecondary">
          создано:
          {" "}
          <DateDisplayWithDatetimeTooltip
            field="created_at"
            row={data.item}
            schema={AbsenceEventSchema.created_at}
            config={{ utcToLocal: true }}
            />
          {", "}
          <DictionarySelectDisplay
            field="created_by"
            row={data.item}
            schema={AbsenceEventSchema.created_by}
            />
          
        </Typography>
        
        {!!data.item.cancelled_at &&
          <Typography variant="caption" color="textSecondary">
            отменено:
            {" "}
            <DateDisplayWithDatetimeTooltip
              field="cancelled_at"
              row={data.item}
              schema={AbsenceEventSchema.cancelled_at}
              config={{ utcToLocal: true }}
              />
            {", "}
            <DictionarySelectDisplay
              field="cancelled_by"
              row={data.item}
              schema={AbsenceEventSchema.cancelled_by}
              />

          </Typography>}

        <Divider />

        <FormControlsForFields
          data={data.item}
          onChange={(o,c) => data.update(c)}
          schema={AbsenceEventSchema}
          fields={[
            ["owner_id", { disabled: true }],
            ["start_date", { disabled: true }],
            data.item.end_date !== data.item.start_date ?
              ["end_date", { disabled: true }]
              : null,
            data.item.comment ? ["comment", { disabled: true }] : null,
            data.item?.approval_log ? ["approval_log", { disabled: true }] : null,
          ]}
          />

        
      </FormGrid>
    </SimpleDialog>
  );
}


interface CancelProps {
  data: EditItemProps<Pick<AbsenceEvent, "_id" | "comment" | "kind">>;
}

export const CancelAbsenceDialog = (props: CancelProps) => {
  const { data } = props;
  if(!data.item) {
    return null;
  }

  return (
    <SimpleDialog
      {...data.dialog}
      maxWidth="xs"
      dialogTitle={"Отмена"}
      noFullscreen
      submitOnModEnter
      saveLabel="отменить"
      save={() => data.save()}
      cancelLabel="назад">
      <FormGrid columns="1fr" noMargin>
        <FormControlsForFields
          data={data.item}
          onChange={(o,c) => data.update(c)}
          schema={AbsenceEventSchema}
          fields={[
            ["owner_id", { disabled: true }],
            ["kind", { disabled: true }],
            ["start_date", { disabled: true }],
            (data.item as any).start_date !== (data.item as any).end_date ? ["end_date", { disabled: true }] : null,
          ]}
          />
        <FormControlsForFields
          data={data.item}
          onChange={(o,c) => data.update(c)}
          schema={AbsenceEventSchema}
          fields={[
            ["comment", { controlProps: { autoFocus: true }}],
          ]}
          />
      </FormGrid>
    </SimpleDialog>
  );
}





interface CreateProps {
  data: EditItemProps<Partial<AbsenceEvent>>;
}

export const CreateAbsenceDialog = (props: CreateProps) => {
  const { data } = props;

  const [isMultidayOn, setIsMultidayOn] = useState<boolean>(false);
  const isMultidayActive = isMultidayOn;

  const setMultidayOn = () => {
    setIsMultidayOn(true);
    data.update({ end_date: data.item?.end_date || data.item?.start_date });
  }

  useEffect(() => {
    if(!data.dialog.isOpen) {
      setIsMultidayOn(false);
    } else if(data.item?.start_date && data.item?.end_date && data.item.start_date !== data.item.end_date) {
      setIsMultidayOn(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.dialog.isOpen]);

  if(!data.item) {
    return null;
  }

  return (
    <SimpleDialog
      {...data.dialog}
      maxWidth="xs"
      dialogTitle={getAbsenceKindTitle(data.item?.kind)}
      noFullscreen
      submitOnModEnter
      save={() => data.save()}>
      <FormGrid columns="1fr" noMargin>

        <FormControlsForFields
          data={data.item}
          onChange={(o,c) => data.update(c)}
          schema={AbsenceEventSchema}
          fields={[
            ["owner_id", { disabled: true }],
            ["kind", { autoComplete: true, controlProps: { autoFocus: true}}],
            ["start_date"],
            isMultidayActive ? ["end_date", { controlProps: { autoFocus: true }}] : null,
            data.item?.kind !== "remote" ? ["comment"] : null,
          ]}
          />

        {!isMultidayActive &&
          <Buttons style={{ justifyContent: "flex-end" }}>
            <PseudoLink2 onClick={() => setMultidayOn()}>на несколько дней</PseudoLink2>
          </Buttons>}
        
      </FormGrid>
    </SimpleDialog>
  );
}
